import React, { useState, useRef, useEffect, useCallback } from 'react'
import { X, SendHorizonal, Clock, Video, VideoOff, Mic, MicOff, Target, PhoneOff } from 'lucide-react'
import { useTranslation } from '@/hooks/useTranslation'
import { Card, CardContent } from '@/components/ui/card'
import ChatTextarea from '@/components/Common/ChatTextarea'
import ParametersSummaryButton from '@/components/Common/ParametersSummaryButton'
import ReactMarkdown from 'react-markdown'
import { useRoleChatStore } from '@/stores/chatStore'
import useLiveAvatar from '@/hooks/useLiveAvatar'
import { useElevenLabsConversation } from '@/hooks/useElevenLabsConversation'
import { useSelfCamera } from '@/hooks/useSelfCamera'
import Modal from '@/components/Common/Modal'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import { gql } from 'graphql-request'
import { cn } from '@/lib/utils'
import { module2Store } from '@/pages/Module2/module2Store'
import { meteringStore, isInsufficientMinutes } from '@/pages/Module2/meteringStore'
import { isSubscriptionRequired, subscriptionStore } from '@/pages/Module2/subscriptionStore'
import { useToast } from '@/hooks/useToast'
import { observer } from 'mobx-react-lite'

interface RolePlayCard {
  id: string
  targetId?: number
  title: string
  playedTimes: number
  rating: number
  instructions?: string // payload.content - roleplay instructions
  avatarId?: number // linked Avatar entity id
  avatarName?: string
  avatarDescription?: string
  agentId?: string // ElevenLabs agent ID from linked avatar
  language?: string // BCP-47 language code for ElevenLabs override
}

export interface RolePlayMessage {
  id: string
  role: 'user' | 'ai'
  content: string
  timestamp: number
}

// `camera` is the user-facing "Video" mode: the voice call plus the learner's own camera.
// `video` is the HeyGen live avatar, not reachable from the UI and left as it is.
export type RolePlayMode = 'text' | 'voice' | 'video' | 'camera'

interface RolePlaySidebarProps {
  isOpen: boolean
  onClose: () => void
  card: RolePlayCard | null
  onStartRolePlay: () => void
  onRolePlayEnd?: (messages: RolePlayMessage[]) => void
  timerDuration?: number // timer duration in seconds, default 300
  flowId?: number // flowId for chat connection, default 10
  mode?: RolePlayMode // roleplay mode: text, voice, video (HeyGen) or camera (voice + own camera)
  objectives?: string[] // objectives from the coach welcome payload; falls back to heuristic/defaults
  onSwitchMode?: (mode: RolePlayMode) => void // reopen the same role play in another mode (camera denied → voice)
}

const WAVE_BARS = [
  { delay: '-1.1s', height: '40%' },
  { delay: '-0.9s', height: '60%' },
  { delay: '-0.7s', height: '85%' },
  { delay: '-0.5s', height: '100%' },
  { delay: '-0.3s', height: '75%' },
  { delay: '-0.1s', height: '55%' },
  { delay: '-0.6s', height: '70%' },
  { delay: '-0.4s', height: '90%' },
  { delay: '-0.8s', height: '65%' },
  { delay: '-0.2s', height: '80%' },
  { delay: '-1.0s', height: '45%' },
  { delay: '-0.5s', height: '60%' },
  { delay: '-0.3s', height: '75%' },
  { delay: '-0.7s', height: '50%' },
  { delay: '-0.9s', height: '35%' },
  { delay: '-1.1s', height: '55%' },
]

const extractObjectives = (instructions?: string): string[] => {
  if (!instructions) {
    return ['Sound calm and specific', 'Use one relevant reference', 'Ask permission briefly', 'Earn the first operational question']
  }

  const objectiveLines = instructions
    .split(/\r?\n/)
    .map((line) => line.replace(/^[-*•\d.)\s]+/, '').trim())
    .filter((line) => line.length > 12 && line.length < 90)
    .slice(0, 4)

  return objectiveLines.length > 0 ? objectiveLines : ['Sound calm and specific', 'Use one relevant reference', 'Ask permission briefly', 'Earn the first operational question']
}

const parseAvatarDescription = (description?: string): { role?: string; company?: string } => {
  if (!description?.trim()) return {}
  const parts = description.split(' · ').map((part) => part.trim()).filter(Boolean)
  if (parts.length >= 2) {
    return { role: parts[0], company: parts.slice(1).join(' · ') }
  }
  return { role: description.trim() }
}

const formatMessageTime = (timestamp: Date | string | number) => {
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })
}

const RolePlaySidebar: React.FC<RolePlaySidebarProps> = ({ isOpen, onClose, card, onStartRolePlay, onRolePlayEnd, timerDuration = 1200, flowId = 11, mode = 'text', objectives: coachObjectives, onSwitchMode }) => {
  const { t } = useTranslation()
  const { showError } = useToast()
  const [message, setMessage] = useState('')
  const [localMessages, setLocalMessages] = useState<RolePlayMessage[]>([])
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const formRef = useRef<HTMLFormElement>(null)
  const videoMessagesRef = useRef<RolePlayMessage[]>([])
  const [hasInitialized, setHasInitialized] = useState(false)
  // Connecting is async; the user can close the panel while it is still in flight.
  const isOpenRef = useRef(isOpen)
  isOpenRef.current = isOpen
  const elevenLabsMessagesRef = useRef<RolePlayMessage[]>([])
  // Voice and camera modes share the whole ElevenLabs pipeline; only the picture differs.
  const usesVoiceCall = mode === 'voice' || mode === 'camera'

  // Use role chat store for text mode (isolated from main chat)
  const { messages: chatMessages, sendMessage: sendChatMessage, initSocket, disconnectSocket, isConnected, isLoading: chatIsLoading, isReady, clearMessages } = useRoleChatStore()

  // ElevenLabs voice mode: transcript handler
  const handleElevenLabsTranscript = useCallback((msg: { role: 'user' | 'assistant'; content: string }) => {
    const rolePlayMsg: RolePlayMessage = {
      id: `el-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      role: msg.role === 'user' ? 'user' : 'ai',
      content: msg.content,
      timestamp: Date.now(),
    }
    elevenLabsMessagesRef.current = [...elevenLabsMessagesRef.current, rolePlayMsg]
    setLocalMessages((prev) => [...prev, rolePlayMsg])
    // Don't forward to socket during conversation — Microfrank would process
    // them through the flow and generate duplicate responses.
    // Messages are pushed to the store on disconnect for history saving.
  }, [])

  // Fetch ElevenLabs signed URL from server — uses avatar's agentId when available
  const getSignedUrl = useCallback(async () => {
    const agentId = card?.agentId
    // Read at call time: the metered session is started just before connecting. Null
    // for B2B users, who are not gated.
    const meteredSessionId = meteringStore.sessionId
    const query = agentId
      ? gql`
          query GetElevenLabsSignedUrl($agentId: String, $meteredSessionId: Int) {
            getElevenLabsSignedUrl(agentId: $agentId, meteredSessionId: $meteredSessionId) {
              signedUrl
            }
          }
        `
      : gql`
          query GetElevenLabsSignedUrl($meteredSessionId: Int) {
            getElevenLabsSignedUrl(meteredSessionId: $meteredSessionId) {
              signedUrl
            }
          }
        `
    const data = await authorizedGraphQLRequest<{
      getElevenLabsSignedUrl: { signedUrl: string }
    }>(query, agentId ? { agentId, meteredSessionId } : { meteredSessionId })
    if (!data.getElevenLabsSignedUrl?.signedUrl) {
      throw new Error('Failed to get ElevenLabs signed URL')
    }
    return data.getElevenLabsSignedUrl.signedUrl
  }, [card?.agentId])

  // ElevenLabs conversation hook
  const {
    start: startElevenLabs,
    stop: stopElevenLabs,
    isConnected: isElevenLabsConnected,
    isConnecting: isElevenLabsConnecting,
    isSpeaking: isElevenLabsSpeaking,
    setMicMuted,
    isMicMuted,
  } = useElevenLabsConversation({
    getSignedUrl,
    voiceId: '',
    language: card?.language || '',
    instructions: card?.instructions || '',
    onTranscript: handleElevenLabsTranscript,
  })

  // Camera mode: the learner's own camera, requested before anything is metered or connected.
  const camera = useSelfCamera()
  const { request: requestCamera, stop: stopCamera } = camera
  const [cameraDenied, setCameraDenied] = useState(false)
  const selfVideoRef = useRef<HTMLVideoElement>(null)

  // Video mode: capture transcription messages for review + chat history
  const handleVideoMessage = useCallback((msg: { role: 'user' | 'assistant'; content: string }) => {
    const rolePlayMsg: RolePlayMessage = {
      id: `video-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      role: msg.role === 'user' ? 'user' : 'ai',
      content: msg.content,
      timestamp: Date.now(),
    }
    videoMessagesRef.current = [...videoMessagesRef.current, rolePlayMsg]
    setLocalMessages((prev) => [...prev, rolePlayMsg])
  }, [])

  // Video mode hooks
  const {
    startSession: startAvatarSession,
    endSession: endAvatarSession,
    isLoadingSession: isLoadingAvatar,
    stream: videoStream,
    mediaStream: avatarVideoRef,
  } = useLiveAvatar({ language: 'en', onMessage: mode === 'video' ? handleVideoMessage : undefined })

  // Timer: use module2Store for consistency with Module2RolePlay
  const timeLeft = module2Store.remainingTime
  const isTimerRunning = module2Store.isTimerActive

  // Format time as MM:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Disconnect all connections (voice/video also disconnect role chat socket used for session token / Chat History)
  const disconnectAll = useCallback(() => {
    if (mode === 'text') {
      disconnectSocket()
    } else if (usesVoiceCall) {
      stopElevenLabs()
      if (elevenLabsMessagesRef.current.length > 0 && useRoleChatStore.getState().messages.length === 0) {
        const elMessages = elevenLabsMessagesRef.current
          .filter((m) => (m.content || '').trim())
          .map((m) => ({
            id: m.id,
            content: m.content,
            role: m.role,
            timestamp: new Date(m.timestamp),
          }))
        useRoleChatStore.setState({ messages: elMessages })
      }
      disconnectSocket()
    } else if (mode === 'video') {
      endAvatarSession()
      // Push video transcriptions to role chat store so disconnectSocket saves them to history
      if (videoMessagesRef.current.length > 0 && useRoleChatStore.getState().messages.length === 0) {
        const videoMessages = videoMessagesRef.current
          .filter((m) => (m.content || '').trim())
          .map((m) => ({
            id: m.id,
            content: m.content,
            role: m.role,
            timestamp: new Date(m.timestamp),
          }))
        useRoleChatStore.setState({ messages: videoMessages })
      }
      disconnectSocket()
    }
    setHasInitialized(false)
    module2Store.setIsTimerActive(false)
    module2Store.resetTimer()
    // Returns the unused seconds and refreshes the balance in the module rail.
    meteringStore.end()
  }, [mode, usesVoiceCall, disconnectSocket, endAvatarSession, stopElevenLabs])

  // Handle end roleplay — triggered by the End button, the X/backdrop close, and timer expiry.
  // Any early exit routes through here so a review is always generated from whatever transcript exists
  // (handleRolePlayEnd guards the empty/too-short cases and shows the "quit unexpectedly" notice).
  const handleEndRolePlay = useCallback(async () => {
    // Build messages based on mode (transcript source varies)
    const messagesToPass = usesVoiceCall ? elevenLabsMessagesRef.current : mode === 'video' ? videoMessagesRef.current : localMessages

    // ElevenLabs voice: push transcript to role chat store so disconnectSocket saves them
    if (usesVoiceCall && messagesToPass.length > 0 && useRoleChatStore.getState().messages.length === 0) {
      const elMessages = messagesToPass
        .filter((m) => (m.content || '').trim())
        .map((m) => ({
          id: m.id,
          content: m.content,
          role: m.role,
          timestamp: new Date(m.timestamp),
        }))
      useRoleChatStore.setState({ messages: elMessages })
    }

    // Video fallback: push video transcriptions to role chat store so disconnectSocket saves them to history
    if (mode === 'video' && messagesToPass.length > 0 && useRoleChatStore.getState().messages.length === 0) {
      const videoMessages = messagesToPass
        .filter((m) => (m.content || '').trim())
        .map((m) => ({
          id: m.id,
          content: m.content,
          role: m.role,
          timestamp: new Date(m.timestamp),
        }))
      useRoleChatStore.setState({ messages: videoMessages })
    }

    if (onRolePlayEnd) {
      onRolePlayEnd(messagesToPass)
    }
    stopCamera()
    disconnectAll()
    onClose()
  }, [onRolePlayEnd, localMessages, disconnectAll, onClose, mode, usesVoiceCall, stopCamera])

  // Timer countdown effect (use module2Store like Module2RolePlay)
  useEffect(() => {
    if (!isTimerRunning || timeLeft <= 0) return

    const timerId = setInterval(() => {
      const next = module2Store.remainingTime - 1
      if (next <= 0) {
        handleEndRolePlay()
        return
      }
      module2Store.setRemainingTime(next)
    }, 1000)

    return () => clearInterval(timerId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTimerRunning, timeLeft])

  // Initialize based on mode when sidebar opens
  useEffect(() => {
    if (isOpen && card && !hasInitialized) {
      const initializeRolePlay = async () => {
        // Camera first, before any minutes are reserved or anything connects: a refusal
        // then costs nothing and the fallback to voice is a clean restart (design D4).
        if (mode === 'camera') {
          const outcome = await requestCamera()
          if (!isOpenRef.current) {
            stopCamera()
            return
          }
          if (outcome === 'denied') {
            setCameraDenied(true)
            return
          }
        }

        // Before anything connects: for a B2C user this reserves the minutes and opens
        // the session the voice and avatar tokens are gated on. B2B users get
        // "not metered" back and carry on exactly as before.
        let allowedSeconds: number | null = null
        const moduleId = module2Store.activeModuleId
        if (moduleId !== null) {
          try {
            const started = await meteringStore.start(moduleId, mode)
            if (started.metered) allowedSeconds = started.allowedSeconds
            // Closed while the start was in flight: the close found no session to end,
            // so end it here or it would run, and be paid for, to its allowed end.
            if (!isOpenRef.current) {
              meteringStore.end()
              return
            }
          } catch (error) {
            if (isInsufficientMinutes(error)) {
              meteringStore.openOffer()
              meteringStore.refreshBalances()
              onClose()
              return
            }
            // The subscription ended (or no longer covers this module) since the page
            // loaded: re-reading it brings up the plans.
            if (isSubscriptionRequired(error)) {
              subscriptionStore.refresh()
              meteringStore.refreshBalances()
              onClose()
              return
            }
            // A metered module cannot run without its session: the media tokens would
            // be refused. Anything else must not be broken by a metering hiccup.
            if (module2Store.activeModuleConfig?.metered) {
              console.error('Failed to start metered roleplay session:', error)
              showError(t('metering.startFailed'))
              onClose()
              return
            }
          }
        }

        const query: Record<string, string | number> = {
          flowId: flowId,
          initialState: JSON.stringify({
            rolePlayNumber: String(card.id),
          }),
        }

        if (mode === 'text') {
          // Text mode: WebSocket chat only
          clearMessages()
          await initSocket(query, async () => {
            await sendChatMessage('', 'system')
          })
        } else if (usesVoiceCall) {
          // Voice (and camera, which only adds the learner's own picture): init role chat socket so backend creates a session (Chat History) and mints a real
          // session token, then start the ElevenLabs conversation. Without this the token stays null,
          // the transcript is never persisted, and the review links to an orphan token ("Session not found").
          elevenLabsMessagesRef.current = []
          await initSocket(query, async () => {})
          await startElevenLabs()
        } else if (mode === 'video') {
          // Video: init role chat socket so backend creates session (Chat History), then start avatar
          videoMessagesRef.current = []
          await initSocket(query, async () => {})
          const instructions = card.instructions || ''
          await startAvatarSession({
            instructions: instructions,
            meteredSessionId: meteringStore.sessionId,
          })
        }

        if (allowedSeconds !== null && !isOpenRef.current) {
          meteringStore.end()
          return
        }

        // Start timer when connected (use module2Store). A metered session's clock has
        // been running on the server since the start call, so the countdown picks up
        // from what is left of the allowed time rather than from the full timer.
        module2Store.setRemainingTime(allowedSeconds === null ? timerDuration : meteringStore.remainingNow())
        module2Store.setIsTimerActive(true)
      }

      initializeRolePlay()
      setHasInitialized(true)
    }
  }, [
    isOpen,
    card,
    hasInitialized,
    flowId,
    timerDuration,
    mode,
    usesVoiceCall,
    initSocket,
    sendChatMessage,
    clearMessages,
    startAvatarSession,
    startElevenLabs,
    requestCamera,
    stopCamera,
    onClose,
    showError,
    t,
  ])

  // Leaving the page mid-roleplay skips handleEndRolePlay; the minutes still come back.
  useEffect(() => {
    return () => {
      meteringStore.end()
    }
  }, [])

  // Reset when sidebar closes (disconnect handled by handleEndRolePlay)
  useEffect(() => {
    if (!isOpen) {
      setLocalMessages([])
      videoMessagesRef.current = []
      elevenLabsMessagesRef.current = []
      module2Store.setIsTimerActive(false)
      module2Store.resetTimer()
      setHasInitialized(false)
      setCameraDenied(false)
      stopCamera()
    }
  }, [isOpen, stopCamera])

  // Camera mode: the self view is only mounted once the call is past its connecting state,
  // so attach the stream whenever either the stream or the element changes.
  const cameraCardVisible = mode === 'camera' && camera.state !== 'requesting' && !isElevenLabsConnecting
  useEffect(() => {
    const el = selfVideoRef.current
    if (!el) return
    el.srcObject = camera.stream
    if (camera.stream) el.play().catch(() => {})
  }, [camera.stream, cameraCardVisible])

  // Denied-camera modal: "Use voice instead" reopens the same role play in voice mode.
  const handleCameraFallbackToVoice = useCallback(() => {
    setCameraDenied(false)
    stopCamera()
    onClose()
    onSwitchMode?.('voice')
  }, [stopCamera, onClose, onSwitchMode])

  const handleCameraDeniedClose = useCallback(() => {
    setCameraDenied(false)
    stopCamera()
    onClose()
  }, [stopCamera, onClose])

  // Sync chat messages to local messages for onRolePlayEnd (text mode only)
  useEffect(() => {
    if (mode !== 'text') return
    const convertedMessages: RolePlayMessage[] = chatMessages
      .filter((msg) => msg.role !== 'system')
      .map((msg) => ({
        id: msg.id,
        role: msg.role === 'user' ? 'user' : 'ai',
        content: msg.content,
        timestamp: msg.timestamp instanceof Date ? msg.timestamp.getTime() : Date.now(),
      }))
    setLocalMessages(convertedMessages)
  }, [mode, chatMessages])

  // Scroll to bottom when new messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [chatMessages])

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!message.trim() || chatIsLoading || !isReady) return

    await sendChatMessage(message)
    setMessage('')
  }

  // Get messages for display (filter out system messages)
  const displayMessages = chatMessages.filter((msg) => msg.role !== 'system')
  const objectives = coachObjectives && coachObjectives.length > 0 ? coachObjectives : extractObjectives(card?.instructions)
  const avatarSrc = card?.avatarId ? `${import.meta.env.VITE_API_URL || 'http://localhost:4050'}/api/avatars/${card.avatarId}/image` : '/boss.png'
  const avatarPersona = parseAvatarDescription(card?.avatarDescription)
  const isLive = mode === 'text' ? isConnected || isReady : usesVoiceCall ? isElevenLabsConnected : Boolean(videoStream)
  const modeLabel = mode === 'voice' ? 'voice mode' : mode === 'video' || mode === 'camera' ? 'video mode' : 'text mode'
  const composerPlaceholder = card?.avatarName
    ? `Type your response to ${card.avatarName.split(' ')[0]}…`
    : 'Type your response…'

    return (
    <>
      {/* Backdrop */}
      <div
        className={`
          fixed inset-0 backdrop-blur-sm z-40
          transition-opacity duration-300 ease-out
          ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}
        `}
        onClick={handleEndRolePlay}
      />

      {/* Sidebar: active call panel */}
      <div
        className={`
          fixed top-0 right-0 h-full w-full lg:w-[70%] z-50 font-[Inter]
          transition-transform duration-300 ease-out
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
        `}
      >
        <Card className="relative flex flex-col h-full border-0 shadow-[-8px_0_32px_rgba(15,76,58,0.06)] rounded-none py-0 gap-0 bg-linear-to-b from-[#FCFDFB] to-[#F2F6EE] overflow-hidden">
          <header className="flex items-center justify-between gap-[18px] bg-[#0F4C3A] px-[26px] py-3.5 shrink-0">
            <div className="flex items-center gap-3 min-w-0 text-white">
              <span className="w-2 h-2 rounded-full bg-[#FF5252] shrink-0 animate-pulse shadow-[0_0_0_5px_rgba(255,82,82,0.15)]" aria-label="Live" />
              <div className="min-w-0">
                <div className="text-sm font-semibold text-white tracking-[-0.2px] truncate">
                  <span className="text-[#C5F26A] font-bold mr-1">#{card?.id || '1'}</span>
                  {card?.title || 'Role Play'}
                </div>
                <div className="text-[11px] text-white/65 mt-0.5">{t('main.sidebar.title')} · construction sales · {modeLabel}</div>
              </div>
            </div>

            <div className="flex items-center gap-2.5">
              <div className={cn('flex items-center gap-1.5 bg-white/8 text-white/95 text-[13px] font-semibold tabular-nums px-3 py-1.5 rounded-md', timeLeft <= 30 && 'bg-[#DC4747] animate-pulse')}>
                <Clock size={14} className="text-[#C5F26A] shrink-0" />
                <span>{formatTime(timeLeft)}</span>
              </div>
              <button
                onClick={handleEndRolePlay}
                className="w-8 h-8 rounded-md flex items-center justify-center text-white/70 hover:bg-white/10 hover:text-white transition-colors"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
          </header>

          {mode === 'text' ? (
            <>
              <div className="flex flex-1 flex-col min-h-0 bg-linear-to-b from-[#FCFDFB] to-[#F2F6EE]">
                <div className="flex items-center gap-3.5 px-4 sm:px-[26px] py-4 bg-white border-b border-[#E8EDE9] shrink-0">
                  <div className="w-[52px] h-[52px] rounded-xl overflow-hidden shrink-0 shadow-[0_4px_12px_rgba(15,76,58,0.12)]">
                    <img
                      src={avatarSrc}
                      alt={card?.avatarName || 'Role play persona'}
                      className="block w-full h-full object-cover"
                      onError={(e) => {
                        const img = e.target as HTMLImageElement
                        if (!img.src.endsWith('/boss.png')) img.src = '/boss.png'
                      }}
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-[17px] font-bold text-[#0F4C3A] tracking-[-0.3px] leading-[1.15]">
                      <span className="truncate">{card?.avatarName || card?.title || 'Role Play'}</span>
                      <span className="w-2 h-2 rounded-full bg-[#A4D447] shrink-0 animate-[roleplay-online-blink_1.6s_ease-in-out_infinite]" aria-label="In call" />
                    </div>
                    {(avatarPersona.role || avatarPersona.company) && (
                      <div className="text-[12.5px] text-[#6B7D72] mt-0.5 truncate">
                        {avatarPersona.role}
                        {avatarPersona.role && avatarPersona.company && ' · '}
                        {avatarPersona.company && <strong className="text-[#0F4C3A] font-medium">{avatarPersona.company}</strong>}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto px-4 sm:px-[26px] pt-5 sm:pt-7 pb-4 sm:pb-[18px] flex flex-col gap-3.5 min-h-0">
                  {!isReady && isOpen && (
                    <div className="flex flex-1 flex-col items-center justify-center gap-3 sm:gap-4">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 border-4 border-[#0F4C3A] border-t-transparent rounded-full animate-spin" />
                      <p className="text-[#6B7D72] text-sm sm:text-base">Connecting...</p>
                    </div>
                  )}

                  {isReady && (
                    <>
                      {displayMessages.length === 0 && (
                        <div className="flex flex-1 items-center justify-center text-[#6B7D72] text-sm sm:text-base">
                          <p>Send a message to start</p>
                        </div>
                      )}

                      {displayMessages.map((msg, index) => {
                        const isUser = msg.role === 'user'
                        const isAi = !isUser
                        const prevMsg = displayMessages[index - 1]
                        const prevIsAi = prevMsg && prevMsg.role !== 'user'
                        const showAiAvatar = isAi && !prevIsAi
                        const showAiSpacer = isAi && prevIsAi

                        return (
                          <div
                            key={msg.id}
                            className={cn(
                              'flex gap-2.5 max-w-[76%] items-end',
                              isUser ? 'self-end flex-row-reverse' : 'self-start',
                            )}
                          >
                            {isAi && (
                              showAiAvatar ? (
                                <div className="w-[30px] h-[30px] rounded-full overflow-hidden shrink-0 bg-[#E8F5EE]">
                                  <img
                                    src={avatarSrc}
                                    alt=""
                                    className="block w-full h-full object-cover"
                                    onError={(e) => {
                                      const img = e.target as HTMLImageElement
                                      if (!img.src.endsWith('/boss.png')) img.src = '/boss.png'
                                    }}
                                  />
                                </div>
                              ) : showAiSpacer ? (
                                <div className="w-[30px] h-[30px] shrink-0 invisible" aria-hidden="true" />
                              ) : null
                            )}
                            <div className="flex flex-col gap-1 min-w-0">
                              <div
                                className={cn(
                                  'px-[15px] py-[11px] text-[14.5px] leading-[1.45] wrap-break-word shadow-[0_1px_2px_rgba(15,76,58,0.04)]',
                                  isUser
                                    ? 'bg-[#0F4C3A] text-white rounded-[14px] rounded-br-[4px]'
                                    : 'bg-white border border-[#E8EDE9] text-[#0F4C3A] rounded-[14px] rounded-bl-[4px]',
                                )}
                              >
                                <ReactMarkdown>{msg.content || ''}</ReactMarkdown>
                              </div>
                              <div className={cn('text-[10.5px] text-[#6B7D72] px-1', isUser && 'text-right')}>
                                {formatMessageTime(msg.timestamp instanceof Date ? msg.timestamp : Date.now())}
                              </div>
                            </div>
                          </div>
                        )
                      })}

                      {chatIsLoading && (
                        <div className="flex gap-2.5 max-w-[76%] self-start items-end">
                          <div className="w-[30px] h-[30px] rounded-full overflow-hidden shrink-0 bg-[#E8F5EE]">
                            <img src={avatarSrc} alt="" className="block w-full h-full object-cover" />
                          </div>
                          <div className="flex flex-col gap-1 min-w-0">
                            <div className="px-[15px] py-[11px] bg-white border border-[#E8EDE9] text-[#0F4C3A] rounded-[14px] rounded-bl-[4px] shadow-[0_1px_2px_rgba(15,76,58,0.04)]">
                              <div className="inline-flex items-center gap-1 py-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#6B7D72] animate-[roleplay-typing-bounce_1.2s_ease-in-out_infinite]" />
                                <span className="w-1.5 h-1.5 rounded-full bg-[#6B7D72] animate-[roleplay-typing-bounce_1.2s_ease-in-out_infinite] [animation-delay:0.2s]" />
                                <span className="w-1.5 h-1.5 rounded-full bg-[#6B7D72] animate-[roleplay-typing-bounce_1.2s_ease-in-out_infinite] [animation-delay:0.4s]" />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  <div ref={messagesEndRef} />
                </div>

                <div className="shrink-0 bg-white border-t border-[#E8EDE9] px-4 sm:px-[22px] pt-3.5 pb-4">
                  <form ref={formRef} onSubmit={handleSendMessage} className="flex items-end gap-2.5 bg-white border-[1.5px] border-[#E8EDE9] rounded-[22px] px-4 sm:px-[18px] py-2 pr-2 focus-within:border-[#A4D447] transition-colors">
                    <ParametersSummaryButton disabled={chatIsLoading || !isReady} parameters={card?.instructions ? { context: card.instructions } : undefined} />
                    <ChatTextarea
                      ref={textareaRef}
                      value={message}
                      onValueChange={setMessage}
                      onEnterPress={() => {
                        if (!message.trim() || chatIsLoading || !isReady) return
                        formRef.current?.requestSubmit()
                      }}
                      placeholder={composerPlaceholder}
                      disabled={!isReady}
                      rows={1}
                      maxHeight={120}
                      className="min-h-[22px] max-h-[120px] flex-1 border-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:border-0 px-0 py-2 text-[14.5px] leading-[1.4] text-[#0F4C3A] placeholder:text-[#95A39A] resize-none"
                    />
                    <button
                      type="submit"
                      disabled={!message.trim() || chatIsLoading || !isReady}
                      className="w-[38px] h-[38px] shrink-0 rounded-full bg-[#C5F26A] text-[#073529] flex items-center justify-center hover:bg-[#B5E558] disabled:bg-[#F1F3F0] disabled:text-[#B5BFB8] disabled:cursor-not-allowed transition-colors"
                      aria-label="Send"
                    >
                      <SendHorizonal size={16} />
                    </button>
                  </form>
                </div>
              </div>
            </>
          ) : (
          <CardContent
            className={cn(
              'flex-1 overflow-y-auto px-4 pt-4 pb-4 flex flex-col items-center min-h-0',
              mode === 'camera' ? 'justify-start sm:px-6 sm:pt-6 sm:pb-6 lg:justify-center' : 'justify-center sm:px-10 sm:pt-[38px] sm:pb-7',
            )}
          >
            {mode === 'voice' &&
              (() => {
                const voiceConnecting = isElevenLabsConnecting
                const voiceConnected = isElevenLabsConnected
                const voiceSpeaking = isElevenLabsSpeaking
                return (
                  <div className="flex flex-col items-center justify-center h-full min-h-0">
                    {/* Connecting state */}
                    {voiceConnecting && (
                      <div className="flex flex-col items-center gap-3 sm:gap-4">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 border-4 border-[#0F4C3A] border-t-transparent rounded-full animate-spin"></div>
                        <p className="text-[#6B7D72] text-sm sm:text-base">Connecting voice...</p>
                      </div>
                    )}

                    {/* Voice Avatar */}
                    {!voiceConnecting && (
                      <div className="flex flex-1 min-h-0 w-full flex-col items-center justify-center">
                        <div className="relative h-full max-h-full w-auto max-w-full aspect-square flex items-center justify-center">
                          <span className="absolute inset-0 rounded-[36px] border-[2.5px] border-[#C5F26A] pointer-events-none animate-[roleplay-ring-pulse_2.4s_ease-out_infinite]" />
                          <span className="absolute inset-0 rounded-[36px] border-[2.5px] border-[#C5F26A] pointer-events-none animate-[roleplay-ring-pulse_2.4s_ease-out_infinite] [animation-delay:0.8s]" />
                          <span className="absolute inset-0 rounded-[36px] border-[2.5px] border-[#C5F26A] pointer-events-none animate-[roleplay-ring-pulse_2.4s_ease-out_infinite] [animation-delay:1.6s]" />
                          <div className="relative w-[calc(100%-20px)] h-[calc(100%-20px)] rounded-2xl sm:rounded-[32px] bg-linear-to-br from-[#E5F0E9] to-[#C9DECF] overflow-hidden border-4 border-white shadow-[0_16px_48px_rgba(15,76,58,0.24),0_0_0_1px_rgba(15,76,58,0.06)]">
                            <img
                              src={avatarSrc}
                              alt="Voice Chat Avatar"
                              className="block w-full h-full object-cover"
                              onError={(e) => {
                                const img = e.target as HTMLImageElement
                                if (!img.src.endsWith('/boss.png')) img.src = '/boss.png'
                              }}
                            />
                            <div className="absolute top-2 right-2 sm:top-3.5 sm:right-3.5 inline-flex items-center gap-1 sm:gap-1.5 rounded-full border border-white/10 bg-[rgba(15,30,22,0.65)] px-2 sm:px-[11px] py-1 sm:py-1.5 text-[8px] sm:text-[10px] font-bold uppercase tracking-[1px] sm:tracking-[1.3px] text-white backdrop-blur">
                              <span className="w-1.5 h-1.5 rounded-full bg-[#FF5252] animate-pulse" />
                              LIVE
                            </div>
                            {(card?.avatarName || avatarPersona.role || avatarPersona.company) && (
                              <div className="absolute inset-x-0 bottom-0 px-3 sm:px-[26px] pt-8 sm:pt-12 pb-3 sm:pb-6 text-white bg-linear-to-b from-transparent via-[rgba(15,30,22,0.55)] to-[rgba(15,30,22,0.92)]">
                                {card?.avatarName && (
                                  <div className="text-sm sm:text-[32px] font-bold tracking-[-0.3px] sm:tracking-[-0.6px] leading-[1.1] mb-0.5 sm:mb-1">{card.avatarName}</div>
                                )}
                                {(avatarPersona.role || avatarPersona.company) && (
                                  <div className="text-[10px] sm:text-[13px] font-medium leading-tight text-[#C5F26A]">
                                    {avatarPersona.role}
                                    {avatarPersona.role && avatarPersona.company && ' · '}
                                    {avatarPersona.company && <strong className="font-semibold text-[#C5F26A]">{avatarPersona.company}</strong>}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })()}

            {/* ==================== CAMERA MODE (user-facing "Video": voice call + own camera) ==================== */}
            {/* Three columns at desktop width: persona | learner's camera, full height | goals. Stacked below lg. */}
            {mode === 'camera' && (
              <div className="flex w-full min-h-full flex-col gap-4 lg:h-full lg:min-h-0 lg:flex-row lg:items-stretch lg:gap-5" data-testid="camera-layout">
                {/* Persona, directly beside the camera (desktop); below lg it is a picture-in-picture on the camera instead */}
                <div className="hidden shrink-0 lg:flex lg:w-[170px] lg:flex-col lg:items-center lg:justify-center xl:w-[200px] 2xl:w-[230px]" data-testid="persona-pip">
                  <div className="relative w-full aspect-square overflow-hidden rounded-2xl border-2 border-[#C5F26A] bg-linear-to-br from-[#E5F0E9] to-[#C9DECF] shadow-[0_8px_24px_rgba(15,76,58,0.18)]">
                    <img
                      src={avatarSrc}
                      alt={card?.avatarName || 'Role play persona'}
                      className="block w-full h-full object-cover"
                      onError={(e) => {
                        const img = e.target as HTMLImageElement
                        if (!img.src.endsWith('/boss.png')) img.src = '/boss.png'
                      }}
                    />
                    {isElevenLabsSpeaking && (
                      <>
                        <span className="absolute inset-0 rounded-[14px] border-2 border-[#C5F26A] pointer-events-none animate-[roleplay-ring-pulse_1.4s_ease-out_infinite]" />
                        <span className="absolute inset-0 rounded-[14px] border-2 border-[#C5F26A] pointer-events-none animate-[roleplay-ring-pulse_1.4s_ease-out_infinite] [animation-delay:0.7s]" />
                      </>
                    )}
                  </div>
                  {(card?.avatarName || avatarPersona.role) && (
                    <div className="mt-3 min-w-0 max-w-full truncate rounded-full border border-[#E8EDE9] bg-white px-3 py-1.5 text-center text-xs leading-tight shadow-[0_1px_2px_rgba(15,76,58,0.04)]">
                      {card?.avatarName && <span className="font-semibold text-[#0F4C3A]">{card.avatarName}</span>}
                      {card?.avatarName && avatarPersona.role && <span className="text-[#95A39A]"> · </span>}
                      {avatarPersona.role && <span className="text-[#6B7D72]">{avatarPersona.role}</span>}
                    </div>
                  )}
                </div>

                {/* Learner's camera: fills the height between the top bar and the End call bar */}
                <div className="relative flex w-full min-w-0 flex-1 flex-col items-center justify-center lg:h-full lg:min-h-0">
                  {/* Connecting state: camera prompt first, then the voice connection */}
                  {!cameraCardVisible && (
                    <div className="flex flex-col items-center gap-3 py-10 sm:gap-4">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 border-4 border-[#0F4C3A] border-t-transparent rounded-full animate-spin"></div>
                      <p className="text-[#6B7D72] text-sm sm:text-base">{camera.state === 'requesting' ? t('main.sidebar.camera.allow') : t('main.sidebar.camera.connecting')}</p>
                    </div>
                  )}

                  {/* Self camera frame with the wave, LIVE pill and "You" chip overlaid */}
                  {cameraCardVisible && (
                    <div className="relative w-full aspect-[3/4] p-[10px] lg:aspect-auto lg:h-full lg:min-h-0">
                      <span className="absolute inset-0 rounded-[36px] border-[2.5px] border-[#C5F26A] pointer-events-none animate-[roleplay-ring-pulse_2.4s_ease-out_infinite]" />
                      <span className="absolute inset-0 rounded-[36px] border-[2.5px] border-[#C5F26A] pointer-events-none animate-[roleplay-ring-pulse_2.4s_ease-out_infinite] [animation-delay:0.8s]" />
                      <span className="absolute inset-0 rounded-[36px] border-[2.5px] border-[#C5F26A] pointer-events-none animate-[roleplay-ring-pulse_2.4s_ease-out_infinite] [animation-delay:1.6s]" />
                      <div className="relative h-full w-full rounded-2xl sm:rounded-[32px] bg-[#101F18] overflow-hidden border-4 border-white shadow-[0_16px_48px_rgba(15,76,58,0.24),0_0_0_1px_rgba(15,76,58,0.06)]" data-testid="camera-frame">
                        {/* The stream is local only: rendered here, never sent anywhere. Mirrored like a mirror, as self views are. */}
                        <video
                          ref={selfVideoRef}
                          autoPlay
                          muted
                          playsInline
                          aria-label={t('main.sidebar.camera.you')}
                          className={cn('block w-full h-full object-cover [transform:scaleX(-1)]', (!camera.isEnabled || camera.state !== 'active') && 'invisible')}
                        />

                        {/* Camera off: the device stays held, only the picture is dark */}
                        {!camera.isEnabled && camera.state === 'active' && (
                          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-[#101F18] pt-[118px] pb-14 sm:pt-[150px] lg:pt-0 text-white/80">
                            <VideoOff size={34} className="text-white/60" />
                            <span className="text-xs sm:text-sm font-medium">{t('main.sidebar.camera.off')}</span>
                          </div>
                        )}

                        {/* Camera lost mid-call: retried in the background, the voice call keeps running */}
                        {camera.state === 'lost' && (
                          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[rgba(15,30,22,0.78)] pt-[118px] pb-14 sm:pt-[150px] lg:pt-0 text-white backdrop-blur-sm">
                            <div className="w-8 h-8 border-4 border-[#C5F26A] border-t-transparent rounded-full animate-spin" />
                            <span className="text-xs sm:text-sm font-medium px-6 text-center">{t('main.sidebar.camera.lost')}</span>
                          </div>
                        )}

                        {/* Permission revoked: nothing to retry until the user changes it */}
                        {camera.state === 'denied' && (
                          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[rgba(15,30,22,0.78)] pt-[118px] pb-14 sm:pt-[150px] lg:pt-0 text-white backdrop-blur-sm">
                            <VideoOff size={34} className="text-white/60" />
                            <span className="text-xs sm:text-sm font-medium px-6 text-center">{t('main.sidebar.camera.denied_inline')}</span>
                          </div>
                        )}

                        {/* Persona picture-in-picture, top-left: phones and tablets only (the desktop layout has the persona column) */}
                        <div className="absolute top-2 left-2 flex w-[84px] flex-col items-start sm:top-3.5 sm:left-3.5 sm:w-[110px] lg:hidden" data-testid="persona-pip-mobile">
                          <div className="relative w-full aspect-square overflow-hidden rounded-xl border-2 border-[#C5F26A] bg-linear-to-br from-[#E5F0E9] to-[#C9DECF] shadow-[0_8px_24px_rgba(0,0,0,0.35)]">
                            <img
                              src={avatarSrc}
                              alt={card?.avatarName || 'Role play persona'}
                              className="block w-full h-full object-cover"
                              onError={(e) => {
                                const img = e.target as HTMLImageElement
                                if (!img.src.endsWith('/boss.png')) img.src = '/boss.png'
                              }}
                            />
                            {isElevenLabsSpeaking && (
                              <>
                                <span className="absolute inset-0 rounded-[10px] border-2 border-[#C5F26A] pointer-events-none animate-[roleplay-ring-pulse_1.4s_ease-out_infinite]" />
                                <span className="absolute inset-0 rounded-[10px] border-2 border-[#C5F26A] pointer-events-none animate-[roleplay-ring-pulse_1.4s_ease-out_infinite] [animation-delay:0.7s]" />
                              </>
                            )}
                          </div>
                          {(card?.avatarName || avatarPersona.role) && (
                            <div className="mt-1.5 max-w-full truncate rounded-full bg-[rgba(15,30,22,0.75)] px-2.5 py-1 text-[10px] leading-tight text-white backdrop-blur sm:text-[11px]">
                              {card?.avatarName && <span className="font-semibold">{card.avatarName}</span>}
                              {card?.avatarName && avatarPersona.role && <span className="text-white/60"> · </span>}
                              {avatarPersona.role && <span className="text-[#C5F26A]">{avatarPersona.role}</span>}
                            </div>
                          )}
                        </div>

                        {/* LIVE pill, top-right */}
                        <div className="absolute top-2 right-2 sm:top-3.5 sm:right-3.5 inline-flex items-center gap-1 sm:gap-1.5 rounded-full border border-white/10 bg-[rgba(15,30,22,0.65)] px-2 sm:px-[11px] py-1 sm:py-1.5 text-[8px] sm:text-[10px] font-bold uppercase tracking-[1px] sm:tracking-[1.3px] text-white backdrop-blur">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#FF5252] animate-pulse" />
                          LIVE
                        </div>

                        {/* Microphone activity wave and status, overlaid bottom-left; capped in width so it never reaches the "You" chip */}
                        <div
                          className="absolute bottom-2 left-2 max-w-[calc(100%-96px)] inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-[rgba(15,30,22,0.65)] px-3 py-1.5 text-white backdrop-blur pointer-events-none sm:bottom-3.5 sm:left-3.5 sm:max-w-[calc(100%-108px)] sm:px-4 sm:py-2"
                          data-testid="camera-wave"
                        >
                          <div className="flex h-5 shrink-0 items-center justify-center gap-[2px] sm:h-6" aria-label="Audio activity">
                            {WAVE_BARS.map((bar, index) => (
                              <span
                                key={`${bar.delay}-${index}`}
                                className={cn('w-[3px] rounded-sm bg-[#C5F26A] animate-[roleplay-wave_1.2s_ease-in-out_infinite]', !isLive && 'opacity-40 paused')}
                                style={{ height: bar.height, animationDelay: bar.delay }}
                              />
                            ))}
                          </div>
                          <div className="flex min-w-0 items-center gap-1.5 text-[11px] font-semibold sm:text-xs">
                            <span className="w-[7px] h-[7px] shrink-0 rounded-full bg-[#A4D447] animate-pulse" />
                            <span className="truncate">{isLive ? 'Listening now' : 'Waiting for connection'}</span>
                          </div>
                        </div>

                        {/* "You" chip with the mic indicator, bottom-right */}
                        <div className="absolute bottom-2 right-2 sm:bottom-3.5 sm:right-3.5 inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-[rgba(15,30,22,0.65)] px-2.5 sm:px-3 py-1 sm:py-1.5 text-[11px] sm:text-xs font-semibold text-white backdrop-blur">
                          {isMicMuted ? <MicOff size={12} className="text-white/40" /> : <Mic size={12} className="text-[#C5F26A]" />}
                          <span className={cn('w-1.5 h-1.5 rounded-full', isMicMuted ? 'bg-white/30' : 'bg-[#C5F26A] animate-pulse')} />
                          {t('main.sidebar.camera.you')}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Goals, directly beside the camera */}
                <div className="flex shrink-0 flex-col lg:w-[230px] lg:min-h-0 lg:justify-center xl:w-[270px] 2xl:w-[300px]">
                  <div className="w-full max-h-full overflow-y-auto rounded-[14px] border border-[#E8EDE9] bg-white px-[22px] py-4" data-testid="camera-goals">
                    <div className="flex items-center mb-3.5">
                      <div className="flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[1.5px] text-[#0F4C3A]">
                        <Target size={13} />
                        GOAL · {objectives.length} OBJECTIVES
                      </div>
                    </div>
                    <ul className="flex flex-col gap-3">
                      {objectives.map((objective) => (
                        <li key={objective} className="flex items-start gap-[11px] text-sm leading-[1.35] text-[#0F4C3A]">
                          <span className="mt-[7px] w-1.5 h-1.5 rounded-full bg-[#A4D447] shrink-0" />
                          <span>{objective}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* ==================== VIDEO MODE ==================== */}
            {mode === 'video' && (
              <div className="flex flex-col items-center justify-center h-full min-h-0">
                {/* Loading state */}
                {isLoadingAvatar && !videoStream && (
                  <div className="flex flex-col items-center gap-3 sm:gap-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 border-4 border-[#0F4C3A] border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-[#6B7D72] text-sm sm:text-base">Connecting video avatar...</p>
                  </div>
                )}

                {/* Video Stream */}
                {videoStream ? (
                  <div className="relative w-full h-full flex items-center justify-center min-h-[200px]">
                    <video ref={avatarVideoRef} className="rounded-[32px] max-h-full max-w-full object-contain border-4 border-white shadow-[0_16px_48px_rgba(15,76,58,0.24)]" autoPlay playsInline />
                  </div>
                ) : (
                  !isLoadingAvatar && (
                    <div className="flex flex-col items-center gap-4 sm:gap-6">
                      {/* Placeholder avatar */}
                      <div className="relative w-[clamp(300px,46dvh,540px)] h-[clamp(300px,46dvh,540px)] max-w-[calc(100vw-68px)] aspect-square rounded-[32px] bg-linear-to-br from-[#E5F0E9] to-[#C9DECF] overflow-hidden border-4 border-white shadow-[0_16px_48px_rgba(15,76,58,0.24)]">
                        <img src={avatarSrc} alt="Video Chat Placeholder" className="block w-full h-full object-cover" />
                      </div>
                      <div className="flex items-center gap-2 flex-wrap justify-center px-2">
                        <Video size={18} className="text-[#0F4C3A] shrink-0" />
                        <span className="text-xs sm:text-sm text-[#6B7D72]">Waiting for video...</span>
                      </div>
                    </div>
                  )
                )}
              </div>
            )}
          </CardContent>
          )}

          {(mode === 'voice' || mode === 'video') && (
            <div className="shrink-0 flex flex-col items-center px-4 pb-4 sm:px-10 sm:pb-6">
              <div className="flex flex-col items-center gap-2.5 mb-[26px]">
                <div className="flex h-10 items-center justify-center gap-[3px]" aria-label="Audio activity">
                  {WAVE_BARS.map((bar, index) => (
                    <span
                      key={`${bar.delay}-${index}`}
                      className={cn('w-1 rounded-sm bg-[#C5F26A] animate-[roleplay-wave_1.2s_ease-in-out_infinite]', !isLive && 'opacity-40 paused')}
                      style={{ height: bar.height, animationDelay: bar.delay }}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-2 text-sm font-semibold text-[#0F4C3A]">
                  <span className="w-[9px] h-[9px] rounded-full bg-[#A4D447] animate-pulse" />
                  {isLive ? 'Listening now' : 'Waiting for connection'}
                </div>
              </div>

              <div className="w-full max-w-[580px] rounded-[14px] border border-[#E8EDE9] bg-white px-[22px] py-4">
                <div className="flex items-center mb-3.5">
                  <div className="flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[1.5px] text-[#0F4C3A]">
                    <Target size={13} />
                    GOAL · {objectives.length} OBJECTIVES
                  </div>
                </div>
                <ul className="flex flex-col gap-3">
                  {objectives.map((objective) => (
                    <li key={objective} className="flex items-start gap-[11px] text-sm leading-[1.35] text-[#0F4C3A]">
                      <span className="mt-[7px] w-1.5 h-1.5 rounded-full bg-[#A4D447] shrink-0" />
                      <span>{objective}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {(usesVoiceCall || mode === 'video') && (
            <div className="px-[26px] pt-5 pb-6 space-y-2 shrink-0 border-t border-[#E8EDE9] bg-white">
              <div className="flex flex-wrap items-center justify-center gap-3.5">
                {mode === 'camera' && (
                  <>
                    <button
                      type="button"
                      onClick={() => setMicMuted(!isMicMuted)}
                      aria-pressed={isMicMuted}
                      aria-label={isMicMuted ? t('main.sidebar.camera.unmute_mic') : t('main.sidebar.camera.mute_mic')}
                      title={isMicMuted ? t('main.sidebar.camera.unmute_mic') : t('main.sidebar.camera.mute_mic')}
                      className={cn(
                        'w-11 h-11 rounded-full border flex items-center justify-center transition-colors',
                        isMicMuted ? 'border-[#DC4747] bg-[#FDECEC] text-[#DC4747] hover:bg-[#FBDDDD]' : 'border-[#E8EDE9] bg-white text-[#0F4C3A] hover:bg-[#F2F6EE]',
                      )}
                    >
                      {isMicMuted ? <MicOff size={18} /> : <Mic size={18} />}
                    </button>
                    <button
                      type="button"
                      onClick={() => camera.setEnabled(!camera.isEnabled)}
                      aria-pressed={!camera.isEnabled}
                      aria-label={camera.isEnabled ? t('main.sidebar.camera.turn_camera_off') : t('main.sidebar.camera.turn_camera_on')}
                      title={camera.isEnabled ? t('main.sidebar.camera.turn_camera_off') : t('main.sidebar.camera.turn_camera_on')}
                      className={cn(
                        'w-11 h-11 rounded-full border flex items-center justify-center transition-colors',
                        !camera.isEnabled ? 'border-[#DC4747] bg-[#FDECEC] text-[#DC4747] hover:bg-[#FBDDDD]' : 'border-[#E8EDE9] bg-white text-[#0F4C3A] hover:bg-[#F2F6EE]',
                      )}
                    >
                      {camera.isEnabled ? <Video size={18} /> : <VideoOff size={18} />}
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={handleEndRolePlay}
                  className="inline-flex min-w-[140px] items-center justify-center gap-2 rounded-[10px] border border-[#DC4747] bg-[#DC4747] px-5 py-3 text-[13px] font-semibold text-white hover:bg-[#c63a3a] hover:border-[#c63a3a] transition-colors"
                >
                  <PhoneOff size={17} />
                  End call
                </button>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Camera refused before anything was metered or connected: explain, and offer voice */}
      {cameraDenied && mode === 'camera' && (
        <div className="fixed inset-0 z-[60]" data-testid="camera-denied-modal">
          <Modal
            id="camera-denied"
            title={t('main.sidebar.camera.denied_title')}
            onClose={handleCameraDeniedClose}
            content={<p className="text-sm leading-relaxed text-gray-700">{t('main.sidebar.camera.denied_body')}</p>}
            footer={
              <>
                <button
                  type="button"
                  onClick={handleCameraDeniedClose}
                  className="inline-flex items-center justify-center rounded-[10px] border border-[#E8EDE9] bg-white px-4 py-2.5 text-[13px] font-semibold text-[#0F4C3A] hover:bg-[#F2F6EE] transition-colors"
                >
                  {t('main.sidebar.camera.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleCameraFallbackToVoice}
                  className="inline-flex items-center justify-center gap-2 rounded-[10px] bg-[#C5F26A] px-4 py-2.5 text-[13px] font-bold text-[#073529] hover:bg-[#B5E558] transition-colors"
                >
                  <Mic size={15} />
                  {t('main.sidebar.camera.use_voice')}
                </button>
              </>
            }
          />
        </div>
      )}
    </>
  )
}

export default observer(RolePlaySidebar)
